import { useCallback, useEffect, useState } from 'react';
import useBeautyFilterStore from 'stores/beautyFilter/beautyFilterStore';
import { banubaValueToSliderValue, sliderValueToBanubaValue, presetValueToSliderValue, sliderValueToPresetValue, isSymmetricRange } from 'utils/beautyFilterUtils';
import useBeautyFilterBanubaStore from 'stores/beautyFilter/beautyFilterBanubaStore';

const useBeautyFilterSlider = () => {
  const store = useBeautyFilterStore();
  const { beauty, makeup } = store;
  const { selectedItem: selectedMenuItem } = store.menu;
  const { getBeautyValue, getMakeupValue, getPresetValue, isUsingBanuba, isUsingPreset, resetState, resetPresetValue, updateBeautyState, updateMakeupState, updatePresetValue } =
    useBeautyFilterBanubaStore();
  const [banubaRange, setBanubaRange] = useState([0, 1]);
  const [sliderValue, setSliderValue] = useState(0);
  const isSliderVisible =
    (selectedMenuItem.id === 'beauty' && beauty.selectedKey && beauty.selectedSubKey) || (selectedMenuItem.id === 'makeup' && makeup.selectedKey && makeup.selectedSubKey);
  const [isDragging, setIsDragging] = useState(false);

  // key가 변경될 때 셋팅
  useEffect(() => {
    if (!isSliderVisible) return;
    let settings;
    let sliderValue = 0;

    if (selectedMenuItem.id === 'beauty' && beauty.selectedKey === 'preset' && beauty.selectedSubKey) {
      settings = beauty.table['preset'].subTable[beauty.selectedSubKey];
      const presetValue = getPresetValue();
      sliderValue = presetValueToSliderValue(presetValue);
      updatePresetValue(presetValue);
    } else if (selectedMenuItem.id === 'beauty' && beauty.selectedKey && beauty.selectedSubKey) {
      settings = beauty.table[beauty.selectedKey].subTable[beauty.selectedSubKey];
      const banubaValue = getBeautyValue(beauty.selectedKey, beauty.selectedSubKey);
      sliderValue = banubaValueToSliderValue(banubaValue, settings.range);
      updateBeautyState(beauty.selectedKey, beauty.selectedSubKey, banubaValue);
    } else if (selectedMenuItem.id === 'makeup' && makeup.selectedKey && makeup.selectedSubKey) {
      settings = makeup.table[makeup.selectedKey].subTable[makeup.selectedSubKey];
      const banubaValue = getMakeupValue(makeup.selectedKey, makeup.selectedSubKey) || 0.5; // 색을 바꿀 때 기본값 0.5
      sliderValue = banubaValueToSliderValue(banubaValue, settings.range);
      updateMakeupState(makeup.selectedKey, banubaValue, settings.banubaPrefabs);
    }
    if (!settings) return;

    setBanubaRange(settings.range);
    setSliderValue(sliderValue);
  }, [
    selectedMenuItem.id,
    beauty.table,
    makeup.table,
    beauty.selectedKey,
    beauty.selectedSubKey,
    makeup.selectedKey,
    makeup.selectedSubKey,
    isSliderVisible,
    getBeautyValue,
    getMakeupValue,
    updateBeautyState,
    updateMakeupState,
    getPresetValue,
    updatePresetValue,
  ]);

  const handleSliderChange = useCallback(
    (sliderValue: number) => {
      if (selectedMenuItem.id === 'beauty' && beauty.selectedKey === 'preset' && beauty.selectedSubKey) {
        const presetValue = sliderValueToPresetValue(sliderValue);
        const settings = beauty.table[beauty.selectedKey].subTable[beauty.selectedSubKey];
        if (!settings) return;
        if (isUsingBanuba()) {
          CommonUI.showToast(document.getElementById('hMainContainer'), { text: brsc.beauty_filter_quick_toast_2, position: 'top', duration: 2000 });
          resetState();
        }
        updatePresetValue(presetValue);
        useBeautyFilterStore.getState().baData.addUsedItem({ category: 'preset', key: 'preset', subKey: beauty.selectedSubKey, value: presetValue });
      } else if (selectedMenuItem.id === 'beauty' && beauty.selectedKey && beauty.selectedSubKey) {
        const banubaValue = sliderValueToBanubaValue(sliderValue, banubaRange);
        const settings = beauty.table[beauty.selectedKey].subTable[beauty.selectedSubKey];
        if (!settings) return;
        if (isUsingPreset()) {
          CommonUI.showToast(document.getElementById('hMainContainer'), { text: brsc.beauty_filter_quick_toast_1, position: 'top', duration: 2000 });
          resetPresetValue();
        }
        updateBeautyState(beauty.selectedKey, beauty.selectedSubKey, banubaValue);
        useBeautyFilterStore.getState().baData.addUsedItem({ category: 'beauty', key: beauty.selectedKey, subKey: beauty.selectedSubKey, value: banubaValue });
      } else if (selectedMenuItem.id === 'makeup' && makeup.selectedKey && makeup.selectedSubKey) {
        const banubaValue = sliderValueToBanubaValue(sliderValue, banubaRange);
        const settings = makeup.table[makeup.selectedKey].subTable[makeup.selectedSubKey];
        if (!settings) return;
        if (isUsingPreset()) {
          CommonUI.showToast(document.getElementById('hMainContainer'), { text: brsc.beauty_filter_quick_toast_1, position: 'top', duration: 2000 });
          resetPresetValue();
        }
        updateMakeupState(makeup.selectedKey, banubaValue, settings.banubaPrefabs);
        useBeautyFilterStore.getState().baData.addUsedItem({ category: 'makeup', key: makeup.selectedKey, subKey: makeup.selectedSubKey, value: banubaValue });
      }
    },
    [
      selectedMenuItem,
      beauty.table,
      makeup.table,
      beauty.selectedKey,
      beauty.selectedSubKey,
      makeup.selectedKey,
      makeup.selectedSubKey,
      banubaRange,
      isUsingBanuba,
      isUsingPreset,
      resetPresetValue,
      updateBeautyState,
      updateMakeupState,
      updatePresetValue,
      resetState,
    ]
  );

  // input 이벤트 리스너
  const handleInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const sliderValue = parseInt(e.target.value);
      setSliderValue(sliderValue);
      handleSliderChange(sliderValue);
    },
    [setSliderValue, handleSliderChange]
  );

  // 리셋
  const resetToDefault = useCallback(() => {
    const sliderValue = isSymmetricRange(banubaRange) ? 500 : 0;
    setSliderValue(sliderValue);
    handleSliderChange(sliderValue);
  }, [banubaRange, setSliderValue, handleSliderChange]);

  const handleDragStart = useCallback(() => setIsDragging(true), [setIsDragging]);
  const handleDragEnd = useCallback(() => setIsDragging(false), [setIsDragging]);

  return {
    sliderValue,
    isSliderVisible,
    resetToDefault,
    handleInput,
    handleDragStart,
    handleDragEnd,
    isDragging,
  };
};

export default useBeautyFilterSlider;
